import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { computeLayout, type GroupEntry } from './layout';
import { orgChartReducer, type OrgChartState } from './orgChartReducer';
import { fetchCurrentChart, saveCurrentChart, upgradeDoc, OrgChartConflictError } from './orgChartApi';
import type { OrgChartDoc, Selection, SaveStatus } from './types';

const SAVE_RETRY_MAX_ATTEMPTS = 5;
const SAVE_RETRY_BASE_MS = 2000;

const SAVE_REASON_BY_ACTION: Record<string, string | undefined> = {
  AUTO_ARRANGE: 'pre_auto_arrange', IMPORT_DOC: 'pre_import', RESET_TO_BASELINE: 'rollback',
};

const INITIAL_REDUCER_STATE: OrgChartState = {
  doc: null, selection: null, editMode: false, zoom: 'fit',
  undoStack: [], canUndo: false, lastMutationAt: null, error: null,
};

export function useOrgChart() {
  const [state, reducerDispatch] = useReducer(orgChartReducer, INITIAL_REDUCER_STATE);
  const [loadStatus, setLoadStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [conflict, setConflict] = useState<{ doc: OrgChartDoc; version: number } | null>(null);

  const isMountedRef = useRef(true);
  const docRef = useRef(state.doc);
  const versionRef = useRef<number | null>(null);
  const baselineRef = useRef<OrgChartDoc | null>(null);
  const lastActionTypeRef = useRef<string | null>(null);
  const pendingReasonRef = useRef<string | undefined>(undefined);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);
  const savingRef = useRef(false);
  const dirtyDuringSaveRef = useRef(false);
  const saveStatusRef = useRef(saveStatus);
  const editModeRef = useRef(state.editMode);

  useEffect(() => { saveStatusRef.current = saveStatus; }, [saveStatus]);
  useEffect(() => { docRef.current = state.doc; }, [state.doc]);
  useEffect(() => { editModeRef.current = state.editMode; }, [state.editMode]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useCallback((action: any) => {
    lastActionTypeRef.current = action && action.type;
    reducerDispatch(action);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadStatus('loading');
    setLoadError(null);
    (async () => {
      try {
        const { doc, version } = await fetchCurrentChart();
        if (cancelled) return;
        const upgraded = upgradeDoc(doc);
        versionRef.current = version;
        if (baselineRef.current == null) baselineRef.current = upgraded;
        dispatch({ type: 'HYDRATE_DOC', doc: upgraded });
        setLoadStatus('loaded');
      } catch (err) {
        if (cancelled) return;
        setLoadStatus('error');
        setLoadError((err as Error)?.message || 'Failed to load the org chart.');
      }
    })();
    return () => { cancelled = true; };
  }, [reloadNonce, dispatch]);

  const retryLoad = useCallback(() => setReloadNonce(n => n + 1), []);

  const scheduleRetry = useCallback((reason?: string) => {
    if (retryTimerRef.current) return;
    const attempt = retryAttemptRef.current;
    if (attempt >= SAVE_RETRY_MAX_ATTEMPTS) return;
    retryAttemptRef.current = attempt + 1;
    const delay = SAVE_RETRY_BASE_MS * 2 ** attempt;
    retryTimerRef.current = setTimeout(() => { retryTimerRef.current = null; performSave(reason); }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performSave = useCallback((explicitReason?: string, expectedVersionOverride?: number) => {
    if (savingRef.current) {
      dirtyDuringSaveRef.current = true;
      if (explicitReason !== undefined) pendingReasonRef.current = explicitReason;
      return undefined;
    }
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    const reason = explicitReason !== undefined ? explicitReason : pendingReasonRef.current;
    pendingReasonRef.current = undefined;
    const doc = docRef.current;
    if (doc == null) return undefined;
    const expectedVersion = expectedVersionOverride !== undefined ? expectedVersionOverride : versionRef.current;
    savingRef.current = true;
    if (isMountedRef.current) setSaveStatus('saving');
    return saveCurrentChart({ doc, expectedVersion, reason })
      .then((result) => {
        versionRef.current = result.version;
        retryAttemptRef.current = 0;
        if (isMountedRef.current) setSaveStatus(dirtyDuringSaveRef.current ? 'unsaved' : 'saved');
        if (isMountedRef.current) setConflict(null);
      })
      .catch((err) => {
        if (err instanceof OrgChartConflictError) {
          if (isMountedRef.current) { setConflict({ doc: err.doc, version: err.version }); setSaveStatus('conflict'); }
          return;
        }
        if (isMountedRef.current) setSaveStatus('unsaved');
        const status = err && typeof err.status === 'number' ? err.status : null;
        const isDefiniteClientError = status != null && status >= 400 && status < 500;
        if (!isDefiniteClientError) scheduleRetry(reason);
      })
      .finally(() => {
        savingRef.current = false;
        if (dirtyDuringSaveRef.current) { dirtyDuringSaveRef.current = false; performSave(); }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lastActionTypeRef.current === 'HYDRATE_DOC') return;
    if (!editModeRef.current) return;
    if (isMountedRef.current) setSaveStatus('unsaved');
    if (savingRef.current) dirtyDuringSaveRef.current = true;
    const lastActionType = lastActionTypeRef.current;
    if (pendingReasonRef.current === undefined && lastActionType != null) {
      pendingReasonRef.current = SAVE_REASON_BY_ACTION[lastActionType];
    }
  }, [state.doc]);

  const prevEditModeRef = useRef(state.editMode);
  useEffect(() => {
    const wasEditing = prevEditModeRef.current;
    prevEditModeRef.current = state.editMode;
    if (!wasEditing || state.editMode) return;
    if (saveStatusRef.current !== 'unsaved') return;
    performSave();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.editMode]);

  useEffect(() => {
    if (saveStatus === 'saved') return undefined;
    function onBeforeUnload(e: BeforeUnloadEvent) { e.preventDefault(); e.returnValue = ''; }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [saveStatus]);

  const save = useCallback(() => performSave(), [performSave]);
  const undo = useCallback(() => dispatch({ type: 'UNDO' }), [dispatch]);
  const resetToBaseline = useCallback(() => {
    if (baselineRef.current == null) return;
    dispatch({ type: 'RESET_TO_BASELINE', baseline: baselineRef.current });
  }, [dispatch]);
  const importDoc = useCallback((doc: OrgChartDoc) => dispatch({ type: 'IMPORT_DOC', doc: upgradeDoc(doc) }), [dispatch]);

  const resolveConflictReload = useCallback(() => {
    if (!conflict) return;
    versionRef.current = conflict.version;
    pendingReasonRef.current = undefined;
    retryAttemptRef.current = 0;
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    dispatch({ type: 'HYDRATE_DOC', doc: upgradeDoc(conflict.doc) });
    setConflict(null);
    setSaveStatus('saved');
  }, [conflict, dispatch]);

  const resolveConflictOverwrite = useCallback(() => {
    if (!conflict) return undefined;
    return performSave(undefined, conflict.version);
  }, [conflict, performSave]);

  const groups = state.doc ? state.doc.groups : null;
  const sunX = state.doc ? state.doc.sun.x : null;
  const sunY = state.doc ? state.doc.sun.y : null;
  const layout: GroupEntry[] = useMemo(
    () => (state.doc ? computeLayout(groups as OrgChartDoc['groups'], sunX as number, sunY as number) : []),
    [groups, sunX, sunY],
  );

  return {
    doc: state.doc, layout, loading: loadStatus === 'loading', loadError, retryLoad,
    selection: state.selection, editMode: state.editMode, zoom: state.zoom, dispatch,
    canUndo: !!state.canUndo, undo, resetToBaseline, importDoc,
    saveStatus, save, conflict, resolveConflictReload, resolveConflictOverwrite,
  };
}
